import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { faMobile, faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { invalidateTableView } from "actions";

import AgGridTable from "components/AgGrid/AgGridContainer";

import { AgGridTables } from "constants/aggrid";
import { ParentSaleTabs, ParentSaleTabType } from "constants/sale";
import { Settings } from "constants/settings";

import {
  currentSaleSelector,
  getActiveRole,
  getIsOnline,
  getSaleLotsBySaleForBusinessUser,
  getSetting,
  prepareBusinessReportData,
  selectAgencyIdByConsignmentIdLookup,
} from "selectors";

import { useGetBusinessUserReportLinksByAgencyName } from "hooks/useGetBusinessUserReportLinksByAgencyName";

import { ColDef, IDoesFilterPassParams } from "ag-grid-community";

import { getBusinessUserSaleReportsColumns } from "./columnDefs";
import { buildBusinessUserSubNavReportButtons } from "./buildReportButtons";

interface BusinessUserSaleLotsTableComponentProps {
  setIsMobile: Dispatch<SetStateAction<boolean>>;
}

export const BusinessUserSaleLotsTable = ({
  setIsMobile,
}: BusinessUserSaleLotsTableComponentProps): React.JSX.Element => {
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);

  const dispatch = useDispatch();

  const agencyId: number | null =
    useSelector(getSetting(Settings.agencyId)) || null;
  const parentSaleTab: ParentSaleTabType = useSelector(
    getSetting(Settings.parentSaleTab),
  );
  const isOnline: boolean = useSelector(getIsOnline);
  const saleLots: SaleLot[] = useSelector(getSaleLotsBySaleForBusinessUser);
  const agencyIdByConsignmentIdLookup: Record<string, number> = useSelector(
    selectAgencyIdByConsignmentIdLookup,
  );
  const { date, saleyard_name: saleyardName }: Sale =
    useSelector(currentSaleSelector);
  const {
    links,
  }: {
    links: {
      buyer: ReportLink[];
      vendor: ReportLink[];
    };
  } = useSelector(prepareBusinessReportData);
  const activeRole: BusinessUserRole = useSelector(getActiveRole);

  const isCommercialUser: boolean = activeRole.is_commercial_user;
  const isComplianceUser: boolean = activeRole.is_compliance_user;

  const viewingAsBuyer: boolean = parentSaleTab === ParentSaleTabs.BOUGHT;

  const reportLinksByAgencyName: Record<string, MinifiedReportLink[]> =
    useGetBusinessUserReportLinksByAgencyName(links, parentSaleTab);

  const tableName: string = viewingAsBuyer
    ? AgGridTables.BUYER_SALE_LOTS
    : AgGridTables.VENDOR_SALE_LOTS;

  const columnDefinitions: ColDef[] = getBusinessUserSaleReportsColumns(
    isCommercialUser,
    isComplianceUser,
    parentSaleTab,
  );

  const doesExternalFilterPass = ({ data }: IDoesFilterPassParams) => {
    const consignmentAgencyId =
      agencyIdByConsignmentIdLookup[data.consignment.id];
    return Boolean(selectedAgencyId === consignmentAgencyId);
  };

  const isExternalFilterPresent = () => Boolean(selectedAgencyId);

  useEffect(() => {
    if (selectedAgencyId !== agencyId) {
      dispatch(invalidateTableView(tableName));
      setSelectedAgencyId(agencyId);
    }
  }, [agencyId, selectedAgencyId, dispatch, tableName, parentSaleTab]);

  const additionalActionButtions: MultiButtonActionButton[] = [
    {
      title: "Reports",
      subNavItems: buildBusinessUserSubNavReportButtons(
        reportLinksByAgencyName,
      ),
      default: true,
      isDisabled: !isOnline,
      icon: faCaretRight,
    },
    viewingAsBuyer && {
      title: "Mobile View",
      icon: faMobile,
      onClick: () => {
        setIsMobile(true);
      },
      dataTour: "mobileView",
    },
  ].filter(Boolean);

  return (
    <AgGridTable
      downloadFilename={`${date}-${saleyardName}-${
        viewingAsBuyer ? "Buyer" : "Vendor"
      }Report.csv`}
      columnDefs={columnDefinitions}
      rowData={saleLots}
      isExternalFilterPresent={isExternalFilterPresent}
      // @ts-expect-error -- ag-grid v29 types say (node: RowNode) but runtime passes IDoesFilterPassParams; fixed in v33
      doesExternalFilterPass={doesExternalFilterPass}
      tableName={tableName}
      showGlobalSearchFilter={false}
      showSimpleAgencyFilter
      showTextFilter
      additionalActions={additionalActionButtions}
    />
  );
};
